/**
 * InlineDdlSchemaBody — the embedded content for the `CREATE SCHEMA` /
 * `ALTER SCHEMA` editor, mounted directly (no trigger, no Popover chrome) for
 * the Compact VQE popover's `compactDdlBodyArm`.
 *
 * Mode selection (`create` vs `alter`) is driven by the parked statement's
 * leading keyword:
 *   - CREATE SCHEMA …  → create mode (Authorization + IF NOT EXISTS)
 *   - ALTER SCHEMA …   → alter mode (rename — pgsql only)
 *
 * Engine gating: mysql / oracle / sqlite have no DDL schemas — renders a
 * muted one-line notice pointing at databases / users / ATTACH respectively.
 * mssql renders the create form, but ALTER SCHEMA … is the
 * move-table-schema op (Slice D1) — alter mode shows a redirect.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice D5 — Schema UI.
 * Redesign: .superpowers/sdd/2026-07-28-compact-visual-ddl-editor/ (T4a/T5).
 */
import { useMemo } from 'react';

import {
  getCreateSchemaUnsupportedTooltip,
  isCreateSchemaSupported,
} from '../edit/edits/createSchema';
import type { VqeDdlEditContext } from '../edit/editContext';
import {
  SchemaEditFormBody,
  type SchemaFormValues,
} from '../render/common/SchemaEditFormBody';
import {
  parseParkedSchemaStatement,
  type ParsedParkedSchema,
} from './parseParkedSchemaStatement';

function valuesFromParsed(parsed: ParsedParkedSchema): Partial<SchemaFormValues> {
  return {
    name: parsed.name,
    authorization: parsed.authorization ?? '',
    ifNotExists: parsed.ifNotExists,
    renameTo: parsed.renameTo ?? '',
  };
}

function useParsedParkedSchema(
  editContext: VqeDdlEditContext,
  parsedOverride: ParsedParkedSchema | null | undefined,
): ParsedParkedSchema | null {
  return useMemo<ParsedParkedSchema | null>(() => {
    if (parsedOverride !== undefined) return parsedOverride;
    try {
      const buf = editContext.service.getParsedBuffer(editContext.documentId);
      if (!buf) return null;
      const stmt = buf.documentStructure.statements.find(
        (s) => s.id === editContext.statementId,
      );
      if (!stmt) return null;
      const text = buf.parsedDocument.source.slice(
        stmt.range.startOffset,
        stmt.range.endOffset,
      );
      return parseParkedSchemaStatement(text);
    } catch {
      return null;
    }
  }, [parsedOverride, editContext.service, editContext.documentId, editContext.statementId]);
}

export interface InlineDdlSchemaBodyProps {
  readonly editContext: VqeDdlEditContext;
  /** Test seam — overrides the parsed parked-schema (skips the scan). */
  readonly parsedOverride?: ParsedParkedSchema | null;
  /** Called after a successful commit (or Cancel) so the host can react. */
  readonly onDone?: () => void;
  /**
   * Cancel-only escape, when the host wants Cancel and commit to mean
   * DIFFERENT things. Falls back to `onDone` when omitted, so every existing
   * caller keeps the old "commit or cancel both call `onDone`" behaviour.
   * The compact popover passes it to close on Cancel WITHOUT closing on a
   * successful commit (the commit funnels through the app-shell
   * PreviewDialog, which needs this body’s context to stay mounted).
   */
  readonly onCancel?: () => void;
}

/**
 * Embedded body for the Compact VQE popover's `compactDdlBodyArm`, mounted
 * directly with no trigger/Popover chrome.
 */
export function InlineDdlSchemaBody({
  editContext,
  parsedOverride,
  onDone,
  onCancel,
}: InlineDdlSchemaBodyProps) {
  const engine = editContext.service.engineId;
  const parsed = useParsedParkedSchema(editContext, parsedOverride);

  const mode: 'create' | 'alter' = parsed?.action === 'alter' ? 'alter' : 'create';
  const initialValues = parsed ? valuesFromParsed(parsed) : undefined;

  if (!isCreateSchemaSupported(engine)) {
    return (
      <p
        className="text-xs text-muted-foreground"
        data-testid="inline-ddl-schema-unsupported"
      >
        {getCreateSchemaUnsupportedTooltip(engine)}
      </p>
    );
  }

  return (
    <SchemaEditFormBody
      editContext={editContext}
      mode={mode}
      initialValues={initialValues}
      testId="inline-ddl-schema-form"
      onCommitted={() => onDone?.()}
      onCancel={() => (onCancel ?? onDone)?.()}
    />
  );
}
